import {
  useState,
  useRef,
  useImperativeHandle,
  forwardRef,
  ForwardRefRenderFunction,
} from 'react';

import colours from '@common/colours';

const generateNotificationId = () =>
  (Math.random().toString(36) + Date.now().toString(36)).substr(2, 10);

export enum NOTIFICATION_STYLE_TYPES {
  success = 'success',
  warning = 'warning',
  error = 'error',
  default = 'default',
}
type StyleType = `${NOTIFICATION_STYLE_TYPES}`;

type Notification = {
  message: string;
  style: string;
  id: string;
  shouldShow: boolean;
};

type Props = Record<string, unknown>;

export type ConfigType = {
  showNotification: (message: string, style?: StyleType) => void;
};

/**
 * The notification handler can be used to show a notification at the top of the page
 *
 * It requires the component to be added within the tree - ideally at the highest level
 * e.g.:
 *
 * ```
 *   <Meta />
 *   <div className="page-container">
 *     ...
 *   </div>
 *   <NotificationHandler />
 * ```
 *
 * It is handled through a ref which can then be used to show a notification, e.g.:
 *
 * ```
 * const notificationHandler = useRef(null);
 *
 * const onClick = () => {
 *   if (notificationHandler.current) {
 *     notificationHandler.current.showNotification('Button clicked!')
 *   }
 * }
 *
 * return (
 *   <button onClick={onClick} />
 *   <NotificationHandler ref={notificationHandler} />
 * );
 * ```
 *
 * showNotification takes two parameters, message and style. Style defaults to 'default',
 * but can be 'success', 'warning', or 'error' - which show different colours of notification
 */
const NotificationHandler: ForwardRefRenderFunction<ConfigType, Props> = (
  props,
  ref,
) => {
  const [notificationsList, setNotificationsList] = useState<Notification[]>(
    [],
  );

  // This allows notificationsList to be used in setIntervals
  // that would otherwise just use the state at the time of
  // the method being called
  const notificationsListRef = useRef(notificationsList);
  notificationsListRef.current = notificationsList;

  const deleteNotification = (id: string) => {
    const { current: notifications } = notificationsListRef;

    const index = notifications.findIndex(e => e.id === id);
    if (index === -1) {
      return;
    }

    notifications.splice(index, 1);
    setNotificationsList([...notifications]);
  };

  const hideNotification = (id: string) => {
    const { current: notifications } = notificationsListRef;

    const index = notifications.findIndex(e => e.id === id);
    if (index === -1) {
      return;
    }

    notifications[index] = {
      ...notifications[index],
      shouldShow: false,
    };

    setNotificationsList([...notifications]);
    setTimeout(() => {
      deleteNotification(notifications[index].id);
    }, 200);
  };

  const showNotification = (id: string) => {
    const { current: notifications } = notificationsListRef;

    const index = notifications.findIndex(e => e.id === id);
    if (index === -1) {
      return;
    }

    notifications[index] = {
      ...notifications[index],
      shouldShow: true,
    };
    setNotificationsList([...notifications]);
    setTimeout(() => {
      hideNotification(id);
    }, 4000);
  };

  useImperativeHandle(ref, () => ({
    showNotification: (message: string, style: StyleType = 'default') => {
      const id = generateNotificationId();
      setNotificationsList(prevArray => [
        ...prevArray,
        {
          message,
          style,
          id,
          shouldShow: false,
        },
      ]);
      setTimeout(() => {
        showNotification(id);
      }, 0);
    },
  }));

  return (
    <div data-cy="notification-handler">
      {notificationsList.map(notification => (
        <div
          className={
            notification.shouldShow
              ? 'show content-container'
              : 'content-container'
          }
          key={notification.id}
        >
          <div className={`notification-bar ${notification.style}`}>
            <p>{notification.message}</p>
          </div>
        </div>
      ))}
      <style jsx>{`
        .content-container {
          text-align: center;
          position: fixed;
          left: 0;
          right: 0;
          top: -150px;
          z-index: 10000;
          transition: 200ms top ease;
        }

        .content-container.show {
          transition: 500ms top ease;
          top: 0;
        }

        .notification-bar {
          display: inline-block;
          border-radius: 25px;
          margin: 14px;
          padding: 15px 30px;
          text-align: center;
          background-color: ${colours.white};
          box-shadow: 0 2px 20px 0 rgba(18, 18, 18, 0.07);
          word-wrap: break-word;
          white-space: normal;
        }

        @media (max-width: 767px) {
          .notification-bar {
            margin: 5px 14px;
          }
        }

        .default {
          border: 1px solid ${colours.greyCloud};
        }

        .default p {
          color: ${colours.snBlack};
        }

        .success {
          border: 1px solid ${colours.snGreen};
        }

        .success p {
          color: ${colours.snGreen};
        }

        .warning {
          border: 1px solid ${colours.sunset};
        }

        .warning p {
          color: ${colours.sunset};
        }

        .error {
          border: 1px solid ${colours.snOrange};
        }

        .error p {
          color: ${colours.snOrange};
        }

        p {
          display: inline-block;
          margin: 0;
          font-size: 16px;
          letter-spacing: -0.25px;
        }
      `}</style>
    </div>
  );
};

export default forwardRef(NotificationHandler);
